// 4D Tetromino definitions
// Each piece exists in 4D space (x, y, z, w)
// w represents the "dimension" or which pit the block exists in

export interface Block4D {
  x: number;
  y: number;
  z: number;
  w: number;
}

export interface Tetromino4D {
  blocks: Block4D[];
  color: number;
  name: string;
}

// Color palette for different pieces
export const COLORS = {
  CYAN: 0x00f7ff,
  MAGENTA: 0xff00f7,
  YELLOW: 0xf7ff00,
  GREEN: 0x00ff7f,
  ORANGE: 0xff7f00,
  BLUE: 0x007fff,
  RED: 0xff3333,
  PURPLE: 0x9933ff,
  PINK: 0xff66b2,
  TEAL: 0x33cccc,
};

// 4D Tetromino shapes
// These extend into the W dimension, creating pieces that span multiple "realities"
export const TETROMINOES: Tetromino4D[] = [
  // I-piece: Linear in X, spans W
  {
    name: 'I4D',
    color: COLORS.CYAN,
    blocks: [
      { x: 0, y: 0, z: 0, w: 0 },
      { x: 1, y: 0, z: 0, w: 0 },
      { x: 0, y: 0, z: 0, w: 1 },
      { x: 1, y: 0, z: 0, w: 1 },
    ],
  },
  // L-piece: 3D L shape
  {
    name: 'L3D',
    color: COLORS.ORANGE,
    blocks: [
      { x: 0, y: 0, z: 0, w: 0 },
      { x: 1, y: 0, z: 0, w: 0 },
      { x: 0, y: 0, z: 1, w: 0 },
      { x: 0, y: 1, z: 0, w: 0 },
    ],
  },
  // T-piece: 3D T extends into W
  {
    name: 'T4D',
    color: COLORS.PURPLE,
    blocks: [
      { x: 1, y: 0, z: 0, w: 0 },
      { x: 0, y: 0, z: 0, w: 1 },
      { x: 1, y: 0, z: 0, w: 1 },
      { x: 2, y: 0, z: 0, w: 1 },
    ],
  },
  // Cube: 2x2 in XZ
  {
    name: 'Cube',
    color: COLORS.YELLOW,
    blocks: [
      { x: 0, y: 0, z: 0, w: 0 },
      { x: 1, y: 0, z: 0, w: 0 },
      { x: 0, y: 0, z: 1, w: 0 },
      { x: 1, y: 0, z: 1, w: 0 },
    ],
  },
  // Hypercube: 2x2 in XW (spans dimensions)
  {
    name: 'HyperCube',
    color: COLORS.MAGENTA,
    blocks: [
      { x: 0, y: 0, z: 0, w: 0 },
      { x: 1, y: 0, z: 0, w: 0 },
      { x: 0, y: 0, z: 0, w: 1 },
      { x: 1, y: 0, z: 0, w: 1 },
    ],
  },
  // S-piece: 3D zigzag
  {
    name: 'S3D',
    color: COLORS.GREEN,
    blocks: [
      { x: 0, y: 0, z: 0, w: 0 },
      { x: 1, y: 0, z: 0, w: 0 },
      { x: 1, y: 0, z: 1, w: 0 },
      { x: 2, y: 0, z: 1, w: 0 },
    ],
  },
  // Z-piece: 4D zigzag spanning W
  {
    name: 'Z4D',
    color: COLORS.RED,
    blocks: [
      { x: 0, y: 0, z: 0, w: 0 },
      { x: 1, y: 0, z: 0, w: 0 },
      { x: 1, y: 0, z: 0, w: 1 },
      { x: 2, y: 0, z: 0, w: 1 },
    ],
  },
  // Plus: 3D plus shape
  {
    name: 'Plus',
    color: COLORS.BLUE,
    blocks: [
      { x: 1, y: 0, z: 0, w: 0 },
      { x: 0, y: 0, z: 1, w: 0 },
      { x: 1, y: 0, z: 1, w: 0 },
      { x: 2, y: 0, z: 1, w: 0 },
      { x: 1, y: 0, z: 2, w: 0 },
    ],
  },
  // Tower: Vertical piece
  {
    name: 'Tower',
    color: COLORS.TEAL,
    blocks: [
      { x: 0, y: 0, z: 0, w: 0 },
      { x: 0, y: 1, z: 0, w: 0 },
      { x: 0, y: 2, z: 0, w: 0 },
    ],
  },
  // 4D Cross: Extends in X, Z, and W
  {
    name: 'Cross4D',
    color: COLORS.PINK,
    blocks: [
      { x: 1, y: 0, z: 1, w: 0 },
      { x: 0, y: 0, z: 1, w: 1 },
      { x: 1, y: 0, z: 1, w: 1 },
      { x: 2, y: 0, z: 1, w: 1 },
      { x: 1, y: 0, z: 0, w: 1 },
      { x: 1, y: 0, z: 2, w: 1 },
      { x: 1, y: 0, z: 1, w: 2 },
    ],
  },
];

export function getRandomTetromino(): Tetromino4D {
  const index = Math.floor(Math.random() * TETROMINOES.length);
  return cloneTetromino(TETROMINOES[index]);
}

export function cloneTetromino(tetromino: Tetromino4D): Tetromino4D {
  return {
    ...tetromino,
    blocks: tetromino.blocks.map(b => ({ ...b })),
  };
}

// Rotation in XY plane (standard 2D rotation, affects height)
export function rotateXY(blocks: Block4D[]): Block4D[] {
  return blocks.map(b => ({
    x: -b.y,
    y: b.x,
    z: b.z,
    w: b.w,
  }));
}

// Rotation in XZ plane (horizontal rotation in 3D)
export function rotateXZ(blocks: Block4D[]): Block4D[] {
  return blocks.map(b => ({
    x: -b.z,
    y: b.y,
    z: b.x,
    w: b.w,
  }));
}

// Rotation in YZ plane
export function rotateYZ(blocks: Block4D[]): Block4D[] {
  return blocks.map(b => ({
    x: b.x,
    y: -b.z,
    z: b.y,
    w: b.w,
  }));
}

// Rotation in XW plane (4D rotation - swaps X and W dimensions!)
export function rotateXW(blocks: Block4D[]): Block4D[] {
  return blocks.map(b => ({
    x: -b.w,
    y: b.y,
    z: b.z,
    w: b.x,
  }));
}

// Rotation in ZW plane (another 4D rotation)
export function rotateZW(blocks: Block4D[]): Block4D[] {
  return blocks.map(b => ({
    x: b.x,
    y: b.y,
    z: -b.w,
    w: b.z,
  }));
}

// Normalize blocks so minimum coords are 0
export function normalizeBlocks(blocks: Block4D[]): Block4D[] {
  const minX = Math.min(...blocks.map(b => b.x));
  const minY = Math.min(...blocks.map(b => b.y));
  const minZ = Math.min(...blocks.map(b => b.z));
  const minW = Math.min(...blocks.map(b => b.w));

  return blocks.map(b => ({
    x: b.x - minX,
    y: b.y - minY,
    z: b.z - minZ,
    w: b.w - minW,
  }));
}
